import createStore from 'zustand/vanilla';
import { getController, isConcurrentFactMode, removeController, updateController } from './controller';
import {
  DashboardWidget,
  Dependent,
  GraphNode,
  LandingPageDashboard,
  LandingPageDashboardState,
  MoveResult,
  MoveResultResponse,
  NatureDefinition,
  PropertyAccess,
  ReferenceNode,
  ReferenceResult,
  ReferenceTreeNode,
  RemoveNodeResponse,
  SystemObject,
  SystemObjectData,
  SystemObjectDetails,
  SystemObjectRef,
  SystemObjectResult,
  WidgetAlignment,
  WidgetSize,
} from './types';
import { deepCompare, transformProperties } from './utils';

export interface Requester {
  query<T>(op: string, args: any, signal?: AbortSignal): Promise<T>;
  mutate<T>(op: string, args: any): Promise<T>;
}

const roots = new Set<number>();
const expandedNodes = new Set<number>();

/**
 * Tracks whether a node is expanded or collapsed in the sidebar tree.
 * This is used to synchronise expansion state when the system object dialog opens.
 * @param id Node ID
 * @param expanded Whether the node is being expanded (true) or collapsed (false)
 */
export function trackExpandedNode(id: number, expanded: boolean) {
  if (expanded) {
    expandedNodes.add(id);
  } else {
    expandedNodes.delete(id);
  }
}

/**
 * Returns the set of node IDs currently expanded in the sidebar.
 */
export function getExpandedNodes(): Set<number> {
  return expandedNodes;
}

const requester: Requester = {
  mutate() {
    return Promise.reject('No requester configured.');
  },
  query() {
    return Promise.reject('No requester configured.');
  },
};

export const natures: Array<NatureDefinition> = [];

export const nodes = createStore(() => ({} as Record<string, GraphNode>));

/**
 * Configures the requester used for making API calls
 * @param newRequester The new requester to be used
 */
export function configureRequester(newRequester: Requester) {
  Object.assign(requester, newRequester);
}

// --- Lazy, batched, once-per-object IAM permission loading ---------------------
// Tree-row children are loaded without permissions (the co4ChildGi BFF fragment)
// to avoid a per-child IAM check on every expand. When the UI actually needs a
// child's scopes (e.g. to preserve app-link routing inside a permission-gated
// app), loadPermissions resolves them in one batched co4GetPermissions call and
// caches them on the node. A node's permissions being defined (even an empty
// array) marks it as resolved, so each object is fetched at most once.
const pendingPermissionIds = new Set<number>();
const inFlightPermissionIds = new Set<number>();
let permissionFlushScheduled = false;

/**
 * Requests IAM permission scopes for the given system object ids. Ids whose node
 * already has permissions (loaded inline via a focus/nav load or previously
 * fetched) or that are already in-flight are skipped, guaranteeing one fetch per
 * object. All ids requested within the same tick are coalesced into one call.
 */
export function loadPermissions(ids: Array<number>) {
  const state = nodes.getState();

  for (const id of ids) {
    if (typeof id !== 'number') continue;
    const node = state[id];
    if (!node || node.details?.permissions || inFlightPermissionIds.has(id)) {
      continue;
    }
    pendingPermissionIds.add(id);
  }

  if (pendingPermissionIds.size === 0 || permissionFlushScheduled) return;

  permissionFlushScheduled = true;
  queueMicrotask(flushSystemObjectPermissions);
}

async function flushSystemObjectPermissions() {
  permissionFlushScheduled = false;
  if (pendingPermissionIds.size === 0) return;

  const ids = [...pendingPermissionIds];
  pendingPermissionIds.clear();
  ids.forEach((id) => inFlightPermissionIds.add(id));

  try {
    const { systemObjects = [] } = await requester.query<{
      systemObjects: Array<{ id: number; permissions: Array<string> }>;
    }>('co4GetPermissions', { ids });

    for (const so of systemObjects) {
      updateNode(so.id, (node) => ({
        details: { ...node.details, permissions: so.permissions ?? [] },
      }));
    }
  } catch {
    // Leave the ids unresolved so a later trigger can retry; routing falls back to gi.
  } finally {
    ids.forEach((id) => inFlightPermissionIds.delete(id));
  }
}

/**
 * Drops cached permissions for a node so the next loadPermissions call re-fetches
 * them. Call after operations that can change a user's effective rights on an
 * object (e.g. a move that changes inherited permissions).
 */
export function invalidateSystemObjectPermissions(id: number) {
  if (typeof id !== 'number') return;
  inFlightPermissionIds.delete(id);
  const node = findGraphNode(id);
  if (node) {
    updateNode(id, (n) => ({ details: { ...n.details, permissions: undefined } }));
  }
}

/**
 * Compares two system objects for sorting based on their names
 */
function sortNodes(a: SystemObject, b: SystemObject) {
  let nameA = '',
    nameB = '';

  if (a.name && b.name) {
    nameA = a.name;
    nameB = b.name;
  } else {
    nameA = (a.properties?.find((p) => p.label === 'name')?.value as string) || '';
    nameB = (b.properties?.find((p) => p.label === 'name')?.value as string) || '';
  }

  return nameA.toLocaleLowerCase().localeCompare(nameB.toLocaleLowerCase());
}

/**
 * Constructs a system object by transforming properties and recursively handling children
 */
function constructSystemObject(so: SystemObject): SystemObject {
  const item = {
    ...so,
    ...transformProperties(so?.properties ?? []),
    natures: so?.natures ?? [],
    children: so?.children?.sort(sortNodes)?.map(constructSystemObject) ?? [],
    parents: so?.parents ?? [],
  };

  return item;
}

/**
 * Initializes the graph with roots and nature definitions
 * @param objectRoots Array of system objects to be used as roots
 * @param initialNatures Array of nature definitions to initialize
 */
export function initializeGraph(objectRoots: Array<SystemObject>, initialNatures: Array<NatureDefinition>) {
  const rootNodes = objectRoots.map(toNode);
  natures.push(...initialNatures);
  rootNodes.forEach((r) => roots.add(r.id));
  updateNodes(rootNodes);
}

/**
 * Converts a system object to a graph node
 */
function toNode(item: SystemObject): GraphNode {
  const details = constructSystemObject(item);

  return {
    id: item.id,
    childNodes: item?.children?.sort(sortNodes)?.map((m) => m.id) ?? [],
    facts: {},
    loaded: false,
    loading: false,
    deleted: false,
    details,
  };
}

/**
 * Updates nodes in the graph state
 * @param newNodes Array of graph nodes to update
 */
function updateNodes(newNodes: Array<GraphNode>) {
  const snapshot = nodes.getState();
  const update: Record<string, GraphNode> = {};
  const queue = [...newNodes];

  while (queue.length > 0) {
    const node = queue.pop();

    if (node) {
      const existing = snapshot[node.id];

      if (!existing || newNodes.includes(node)) {
        update[node.id] = node;
      } else if (existing && !existing.loaded) {
        update[node.id] = {
          ...existing,
          details: {
            ...existing.details,
            ...node.details,
            children: node.details?.children ?? existing.details.children,
          },
          childNodes: node.childNodes || existing.childNodes,
        };
      }

      const children = node?.details?.children?.map(toNode) ?? [];

      queue.push(...children);
    }
  }

  nodes.setState(update);
}

/**
 * Updates a node in its parent objects' children arrays
 * @param id ID of the node to update
 * @param details Updated system object details
 */
function updateNodeInParents(id: number, details: SystemObject) {
  if (typeof id !== 'number' || !details) return;

  const parents = details?.parents || [];
  for (const parent of parents) {
    updateNode(parent.id, (n) => {
      const children = n?.details?.children?.map((c) => {
        if (c.id === id) {
          return { ...c, ...details };
        }
        return c;
      });

      return {
        details: {
          ...n.details,
          children,
        },
      };
    });
  }
}

/**
 * Resyncs a node by fetching fresh data and updating it in the store
 * @param id ID of the node to resync
 */
async function resyncNode(id: number) {
  const { object: result } = await queryNode(id).catch(() => ({} as SystemObjectData));

  // Update the current node
  updateNode(id, (node) => ({
    ...node,
    details: constructSystemObject(result),
  }));

  // Update the current node in parent objects (children property)
  updateNodeInParents(id, result);
}

/**
 * Updates a specific node by applying a function to its current state
 * @param id ID of the node to update
 * @param fn Function that takes the current node and returns partial updates
 */
function updateNode(id: number, fn: (node: GraphNode) => Partial<GraphNode>) {
  const state = nodes.getState();
  const oldItem = state[id];
  const newItem = fn(oldItem);
  updateNodes([
    {
      ...oldItem,
      ...newItem,
    },
  ]);
}

/**
 * Moves a node by updating its parent relationships
 * @param id ID of the node to move
 * @param parents Array of new parent system objects
 */
function moveNode(id: number, parents: Array<SystemObject>) {
  const state = nodes.getState();
  const node = state[id];

  if (node) {
    const oldParents = node.details?.parents || [];
    const newParents = parents.map((p) => findGraphNode(p.id)!);

    updateNode(id, (node) => ({
      details: {
        ...node.details,
        parents,
      },
    }));

    for (const parent of oldParents) {
      updateNode(parent.id, (node) => ({
        childNodes: node.childNodes.filter((c) => c !== id),
        details: {
          ...node.details,
          children: node.details.children?.filter((c) => c.id !== id),
        },
      }));
    }

    for (const parent of newParents) {
      updateNode(parent.id, (n) => {
        const children = [...(n.details?.children ?? []), node.details].sort(sortNodes);
        return {
          childNodes: children.map((c) => c.id),
          details: {
            ...n.details,
            children,
          },
        };
      });
    }

    // Re-parenting can change inherited permissions, so drop the cached scopes;
    // they are re-fetched on demand (or with the next focus/nav load).
    invalidateSystemObjectPermissions(id);
  }
}

/**
 * Retrieves a graph node by ID, triggering a load if not found
 * @param id ID of the node to retrieve
 */
export function retrieveGraphNode(id: number): GraphNode {
  const state = nodes.getState();
  const node = state[id];

  if (!node) {
    loadGraphNode(id);
  }

  return node;
}

/**
 * Finds a graph node by ID without triggering a load operation
 * @param id ID of the node to find
 */
export function findGraphNode(id?: number): GraphNode | undefined {
  if (typeof id === 'number') {
    const state = nodes.getState();
    return state[id];
  }

  return undefined;
}

/**
 * Finds multiple graph nodes by their IDs
 * @param ids Array of node IDs to find
 */
export function findGraphNodes(ids: Array<number>): Array<GraphNode> {
  const state = nodes.getState();
  return ids.map((id) => state[id]).filter(Boolean);
}

/**
 * Gets all root node IDs from the graph
 */
export function getRootIds() {
  return Array.from(roots);
}

/**
 * Maps graph nodes to their system object representations
 * @param nodes Array of graph nodes to map
 */
export function mapSystemObjects(nodes: Array<GraphNode>) {
  return nodes.map((node) => node.details);
}

/**
 * Gets all root nodes as system objects
 */
export function getRootNodes() {
  const nodes = findGraphNodes(getRootIds());
  return mapSystemObjects(nodes);
}

/**
 * Queries node data from the server
 * @param id ID of the node to query
 * @param anon Whether to include hidden objects
 * @param key Query key to use (default: 'id')
 */
export async function queryNode(id: number, anon = false, key = 'id'): Promise<SystemObjectData> {
  if (typeof id !== 'number') {
    throw new Error('Received not valid id: ' + id);
  }

  return await requester.query<SystemObjectData>('co4CoreGIGet', { [key]: id, hidden: anon });
}

/**
 * Queries node data with children from the server using navGet
 * This includes both node details and children in one call - useful for navigation
 * @param id ID of the node to query
 * @param anon Whether to include hidden objects
 * @returns Promise resolving to SystemObjectData with children included
 */
export async function queryNavNode(id: number, anon = false): Promise<SystemObjectData> {
  if (typeof id !== 'number') {
    throw new Error('Received not valid id: ' + id);
  }

  return await requester.query<SystemObjectData>('co4CoreNavGet', { id, hidden: anon });
}

/**
 * Completes a node using navGet (includes children data) - for navigation contexts
 * @param id ID of the node to complete
 * @param anon Whether to include hidden objects
 */
async function completeNodeWithNav(id: number, anon = false) {
  const { object: details } = await requester
    .query<SystemObjectData>('co4CoreNavGet', { id, hidden: anon })
    .catch((err) => {
      const deleted = !!err?.list?.some((m) => m?.message?.toLowerCase().includes('not found'));
      const denied = !!err?.list?.some((m) => m?.message?.toLowerCase().includes('permission'));

      updateNode(id, () => {
        return {
          ...toNode({ id, name: 'Loading...', natures: [] } as SystemObject),
          id,
          loaded: true,
          loading: false,
          deleted: deleted || denied,
        };
      });
      return { object: {} } as SystemObjectData;
    });

  return {
    loaded: true,
    loading: false,
    details: constructSystemObject(details),
    childNodes: details.children?.sort(sortNodes)?.map((m) => m.id) ?? [],
  } as GraphNode;
}

/**
 * Loads children of a graph node if not already loaded
 * Uses navGet which includes both node details and children in one call
 * @param id ID of the node whose children to load
 * @param anon Whether to include hidden objects
 */
export async function loadGraphChildren(id: number, anon = false) {
  const state = nodes.getState();
  const node = state[id];

  // If node doesn't exist or isn't loaded, use navGet to get both node details and children
  if (!node || !node.loaded) {
    updateNode(id, () => ({
      loading: true,
    }));
    const newState = await completeNodeWithNav(id, anon);
    updateNode(id, () => newState);
    return newState?.details?.children;
  }

  // If node is loaded but children aren't loaded yet, use navGet to get children
  if (!node.loading && node.details.hasChildren && !node.details.children?.length) {
    updateNode(id, () => ({
      loading: true,
    }));
    const { object: details } = await requester.query<SystemObjectData>('co4CoreNavGet', { id, hidden: anon });

    const newState = {
      loaded: true,
      loading: false,
      details: constructSystemObject(details),
      childNodes: details.children?.sort(sortNodes)?.map((m) => m.id) ?? [],
    };

    updateNode(id, () => newState);

    return newState?.details?.children;
  }
  return node?.details?.children;
}

/**
 * Completes a node by loading its full details and children
 * @param id ID of the node to complete
 * @param anon Whether to include hidden objects
 * @param key Query key to use (default: 'id')
 */
async function completeNode(id: number, anon = false, key = 'id') {
  const { object: details } = await queryNode(id, anon, key).catch((err) => {
    const deleted = !!err?.list?.some((m) => m?.message?.toLowerCase().includes('not found'));

    if (key === 'id') {
      updateNode(id, () => {
        return {
          ...toNode({ id, name: 'Loading...', natures: [] } as SystemObject),
          id,
          loaded: true,
          loading: false,
          deleted,
        };
      });
    }
    return { object: {} } as SystemObjectData;
  });

  return {
    loaded: true,
    loading: false,
    details: constructSystemObject(details),
  } as GraphNode;
}

/**
 * Completes multiple nodes by loading their details
 * @param loadedNodes Array of nodes to complete
 * @param anon Whether to include hidden objects
 */
export async function completeNodes(loadedNodes: Array<GraphNode>, anon = true) {
  const unloadedNodes = loadedNodes.filter((child) => !child.loaded && !child.loading);

  if (unloadedNodes.length > 0) {
    const initial = unloadedNodes.reduce((obj, node) => {
      obj[node.id] = {
        ...node,
        loading: true,
      };
      return obj;
    }, {});

    nodes.setState(initial);

    const newStates = await Promise.all(unloadedNodes.map((node) => completeNode(node.id, anon)));
    const snapshot = nodes.getState();
    const updated = unloadedNodes.map((node, i) => {
      const c = snapshot[node.id];
      const s = newStates[i];

      return {
        ...c,
        ...s,
      };
    });

    updateNodes(updated);
  }
}

/**
 * Loads a graph node by its nature instance ID
 * @param niid Nature instance ID
 * @param anon Whether to include hidden objects
 */
export async function loadGraphNodeByNiid(niid: number, anon = false) {
  return await loadGraphNode(niid, anon, 'niid');
}

/**
 * Loads a graph node by its ID or NIID
 * @param id ID of the node to load
 * @param anon Whether to include hidden objects
 * @param key Query key to use (default: 'id') - 'id' or 'niid'
 * @param loadChildren Whether to automatically load children if node has children (default: false)
 */
export async function loadGraphNode(id: number, anon = false, key = 'id', loadChildren = false) {
  if (key === 'id') {
    updateNode(id, () => ({
      loading: true,
    }));
  }

  const newState = await completeNode(id, anon, key);

  if (typeof newState?.details?.id === 'number') {
    updateNode(newState.details.id, () => newState);

    // Automatically load children if requested and node has children
    if (loadChildren && newState.details.hasChildren) {
      // Don't await - let it load in background
      loadGraphChildren(newState.details.id, anon).catch((err) => {
        console.warn('Failed to auto-load children for node', id, err);
      });
    }
  }
  return newState;
}

/**
 * Loads multiple graph nodes and their parents if necessary
 * Uses navGet for nodes to ensure children are loaded for navigation
 * @param ids Array of node IDs to load
 */
export function loadGraphNodes(ids: Array<number>) {
  const current: Array<GraphNode> = [];
  const snapshot = nodes.getState();
  const promises: Array<Promise<void>> = [];

  for (let i = 0; i < ids.length; i++) {
    const id = ids[i];
    const node = snapshot[id];
    current.push(node);

    if (!node?.loaded && !node?.deleted) {
      if (!node?.loading) {
        // Use navGet directly to get both node details and children in one call
        // This ensures childNodes are populated for navigation
        promises.push(
          (async () => {
            updateNode(id, () => ({
              loading: true,
            }));
            const newState = await completeNodeWithNav(id, true);
            updateNode(id, () => newState);

            // load parents of the node if not loaded
            // (when node is visited directly using URL with missing nodes in the path)
            const parents = newState?.details?.parents?.map((v) => v?.id) || [];
            loadGraphNodes(parents);
          })(),
        );
      }

      current.push(
        ...ids.slice(i + 1).map(
          (id): GraphNode => ({
            id,
            loaded: false,
            deleted: false,
            loading: true,
            facts: {},
            childNodes: [],
            details: {
              id,
              hidden: false,
              name: '...',
              images: [],
              icon: undefined,
              natures: [],
              parents: [],
              path: [],
              properties: [],
              dependents: [],
            },
          }),
        ),
      );

      break;
    }
  }

  return current;
}

/**
 * Adds a new node to the graph with specified parents and properties
 * @param parents Array of parent IDs for the new node
 * @param input Partial system object details for the new node
 */
export async function addGraphNode(parents: Array<number>, input: Partial<Omit<SystemObjectDetails, 'parents'>>) {
  const properties = Object.entries(input)?.map(([label, value]) => ({
    label,
    value,
    access: PropertyAccess?.WRITE,
  }));
  const hidden = [undefined, null]?.includes(input?.name);

  const res = await requester.mutate<SystemObjectResult>('co4CreateSystemObject', {
    parents,
    hidden,
    properties,
  });

  let newChild: SystemObject;

  const parentPath = findGraphNode(parents[0])?.details?.path || [];
  const parentObjects = parents.map((id) => ({ id } as SystemObject));

  if (res) {
    const path = parentPath.length ? parentPath.concat(res.object.id) : [];
    newChild = {
      ...input,
      name: input?.name || '',
      id: res?.object?.id,
      natures: [],
      path,
      hidden,
      properties: res?.object?.properties ?? [],
      parents: parentObjects,
    };

    for (const parent of parents) {
      const parentNode = findGraphNode(parent);
      const children = [...(parentNode?.details.children ?? [])].concat(newChild).sort(sortNodes);
      updateNode(parent, (n) => ({
        loaded: n.loaded,
        loading: false,
        details: {
          ...parentNode?.details,
          children,
        },
        childNodes: children.map((c) => c.id),
      }));
    }
  }

  return res;
}

/**
 * Maps a graph node to a dependent representation
 * @param node Graph node with optional blocker ID
 */
function mapDependent(node: GraphNode & { blockerId?: number }): Dependent {
  return {
    soid: node.details.id,
    name: node.details.name,
    icon: node.details.icon,
    nature: typeof node.blockerId === 'number' ? node.details.natures.find((n) => n.id === node.blockerId) : undefined,
  };
}

/**
 * Gets dependents for a node that would prevent its deletion
 * @param id ID of the node to check
 */
export async function getDependents(id: number): Promise<Array<Dependent>> {
  const node = findGraphNode(id);
  const niids = node?.details?.natures?.map((n) => n.id) || [];

  // get the Nature Instance and System Object dependents
  const [niBlockers, soBlockers] = await Promise.all([canDeleteNatureInstances(niids), canDeleteGraphNodes([id])]);

  // load the dependent node details
  const ni = await Promise.all(
    niBlockers.map((id) => loadGraphNodeByNiid(id, true).then((res) => mapDependent({ ...res, blockerId: id }))),
  );

  // filter and only check for those SO blockers that are not in NI blockers
  // SO blockers from co4CanRemoveSystemObjects are SOIDs, so use loadGraphNode with key='id'
  const so = await Promise.all(
    soBlockers.filter((v) => !niBlockers.includes(v)).map((id) => loadGraphNode(id, true)),
  )?.then((res) => res.map(mapDependent));

  return [...ni, ...so];
}

/**
 * Checks if nature instances can be deleted by querying for any blockers
 * @param niids - Array of nature instance IDs to check for deletion
 * @returns Promise resolving to array of blocker IDs that prevent deletion, empty array if deletion is allowed
 */
export async function canDeleteNatureInstances(niids: Array<number>): Promise<Array<number>> {
  if (!niids.length) return [];
  const res = await requester.query<RemoveNodeResponse>('co4CanRemoveNatureInstances', { niids });
  return res?.result?.blockers?.map((b) => b.blocker) ?? [];
}

/**
 * Deletes specified nature instances from a system object
 * @param soid - System object ID containing the nature instances
 * @param niids - Array of nature instance IDs to delete
 * @returns Promise resolving to either array of blocker IDs or the mutation response
 */
export async function deleteNatureInstances(soid: number, niids: Array<number>) {
  const blockers = await canDeleteNatureInstances(niids);
  if (blockers.length > 0) return blockers;

  const res = await requester.mutate<RemoveNodeResponse>('co4RemoveNatureInstances', { niids });

  if (!res.result.success) {
    return res.result.blockers?.map((b) => b.blocker) ?? [];
  }

  updateNode(soid, (prev) => ({
    details: {
      ...prev.details,
      natures: prev.details.natures?.filter((n) => !res.result?.removedNiids?.includes(n.id)),
    },
  }));

  return res;
}

/**
 * Pre-check whether the given nature instances can move to `soid`.
 * Always evaluates the parent/child and co-target closure, not the requested subset.
 * @param niids Nature instance IDs to move
 * @param soid Destination system object ID
 */
export async function canMoveNatureInstances(niids: Array<number>, soid: number): Promise<MoveResult> {
  const res = await requester.query<MoveResultResponse>('co4CanMoveNatureInstances', { niids, soid });
  return res?.result ?? emptyMoveResult();
}

/**
 * Copy inserts on dest; move does that then deletes the source row.
 * Overwrite updates every dest row with that label to the source value.
 */
export type PropertyMigrationOp = 'COPY' | 'MOVE';

export interface PropertyMigration {
  propertyId: number;
  operation: PropertyMigrationOp;
  overwrite: boolean;
}

/**
 * Move the given nature instances to `soid`. `niids` must be exactly the
 * `requiredNiids` set from `canMoveNatureInstances` for this destination.
 * Optional `properties` copy or move source properties in the same mutation.
 * @param niids Closure of nature instance IDs to move
 * @param soid Destination system object ID
 * @param properties Source properties to copy or move; omit or pass [] for natures only
 */
export async function moveNatureInstances(
  niids: Array<number>,
  soid: number,
  properties: Array<PropertyMigration> = [],
): Promise<MoveResult> {
  const res = await requester.mutate<MoveResultResponse>('co4MoveNatureInstances', {
    niids,
    soid,
    properties,
  });
  return res?.result ?? emptyMoveResult();
}

/**
 * Rewrite SystemObjectField rows that point at `fromSoid` so they point at `toSoid`.
 * Rows the caller cannot update are returned as Permission blockers and left unchanged.
 * @param fromSoid System object ID currently targeted by the fields
 * @param toSoid System object ID to retarget the fields to
 */
export async function repointSystemObjectFields(fromSoid: number, toSoid: number): Promise<MoveResult> {
  const res = await requester.mutate<MoveResultResponse>('co4RepointSystemObjectFields', { fromSoid, toSoid });
  return res?.result ?? emptyMoveResult();
}

const emptyMoveResult = (): MoveResult => ({
  success: false,
  requiredNiids: [],
  additionalNiids: [],
});

/**
 * Checks if graph nodes can be deleted by querying for any blockers
 * @param soids - Array of system object IDs to check for deletion
 * @returns Promise resolving to array of blocker IDs that prevent deletion, empty array if deletion is allowed
 */
export async function canDeleteGraphNodes(soids: Array<number>): Promise<Array<number>> {
  if (!soids.length) return [];
  const res = await requester.query<RemoveNodeResponse>('co4CanRemoveSystemObjects', { soids });
  return res?.result?.blockers?.map((b) => b.blocker) ?? [];
}

/**
 * Deletes a graph node and its associated nature instances
 * @param id - System object ID of the node to delete
 * @returns Promise resolving to either array of blocker IDs or the mutation response
 */
export async function deleteGraphNode(id: number) {
  const node = findGraphNode(id);
  if (!node) return [];

  // Delete all nature instances first
  const niids = node.details?.natures?.map((n) => n.id) ?? [];
  if (niids.length) {
    const blockers = await deleteNatureInstances(id, niids);
    if (Array.isArray(blockers) && blockers.length > 0) return blockers;
  }

  // Delete the system object
  const res = await requester.mutate<RemoveNodeResponse>('co4RemoveSystemObjects', {
    soids: [id],
  });

  if (!res.result.success) {
    return res.result.blockers?.map((b) => b.blocker) ?? [];
  }

  // Update node and parent references
  const parents = node.details?.parents ?? [];

  updateNode(id, (prev) => ({
    deleted: true,
    details: {
      ...prev.details,
      natures: [],
    },
  }));

  for (const parent of parents) {
    updateNode(parent.id, (p) => ({
      loaded: p.loaded,
      loading: p.loading,
      details: {
        ...p.details,
        children: p.details.children?.filter((c) => c.id !== id),
      },
      childNodes: p.childNodes.filter((c) => c !== id),
    }));
  }

  return res;
}

/**
 * Loads a fact for a system object using the specified operation
 * @param soid System object ID
 * @param op Operation to execute
 * @param args Arguments for the operation
 * @param force Whether to force reload even if cached
 */
export async function loadFact<T>(
  soid: number | undefined,
  op: string,
  args: Record<string, number | string>,
  force = false,
) {
  // Concurrent mode (dashboard): non-aborting controller so multi-object loads coexist.
  const controllerKey = `fact-${op}-${soid}`;
  const controller = isConcurrentFactMode() ? getController(controllerKey) : updateController(controllerKey);
  const so = findGraphNode(soid);

  if (!force && so?.facts && op in so.facts) {
    return so.facts[op];
  }

  const result = await requester
    .query<T>(op, args, controller.signal)
    .finally(() => removeController(`fact-${op}-${soid}`));

  updateNode(soid, (node) => {
    if (!node) {
      return {
        id: soid,
        loaded: false,
        loading: false,
        childNodes: [],
        details: {
          id: soid,
          name: '...',
          images: [],
          parents: [],
          natures: [],
          path: node?.details?.path ?? [],
          properties: [],
          dependents: [],
          children: undefined,
          hidden: false,
          icon: undefined,
        },
        facts: {
          [op]: result,
        },
      };
    } else {
      return {
        facts: {
          ...node.facts,
          [op]: result,
        },
      };
    }
  });

  return result as T;
}

/**
 * Updates a fact for a system object by executing a mutation
 * @param soid System object ID
 * @param op Operation to execute
 * @param payload Payload for the mutation
 * @param clear Whether to clear the fact from cache after mutation
 */
export async function mutateFact<T>(
  soid: number | undefined,
  op: string,
  payload: Record<string, any>,
  clear: boolean = false,
) {
  const result = await requester.mutate<T>(op, payload);

  if (soid !== undefined) {
    let node = findGraphNode(soid);
    /* clear the facts if the "clear" is true */
    if (clear) {
      const facts = node?.facts ?? {};
      delete facts[op];

      updateNode(soid, (node) => ({
        details: {
          ...node.details,
          natures: node.details.natures?.filter((n) => n.id !== payload?.niid) ?? [],
        },
        facts,
      }));

      // get the updated node and update it in parents
      node = findGraphNode(soid);
      updateNodeInParents(soid, node?.details);
    } else if (!node?.facts || !(op in node?.facts)) {
      /* If op doesn't exist in facts, refresh the natures */
      await resyncNode(soid);
    }
  }
  return result as T;
}

/**
 * Updates system object properties
 * @param id System object ID
 * @param details New property values
 */
async function updateProperties(id: number, details: Partial<SystemObjectDetails>) {
  const current = findGraphNode(id);
  const properties = current?.details?.properties ?? [];

  const newProps = [],
    updatedInputs = [],
    propsToRemove = [];

  Object.entries(details).forEach(([label, value]) => {
    if (!deepCompare(value, current?.details?.[label])) {
      const id = properties.find((p) => p.label === label)?.id;

      if (typeof id === 'number') {
        if (value === undefined || value === null) {
          propsToRemove.push(id);
        } else {
          updatedInputs.push([id, value]);
        }
      } else if (value !== undefined && value !== null) {
        newProps.push({
          label,
          value,
          access: PropertyAccess.WRITE,
        });
      }
    }
  });

  if (propsToRemove.length > 0) {
    await requester.mutate('removeObjectProperties', { ids: propsToRemove });
  }

  if (newProps.length > 0) {
    await requester.mutate('setObjectProperties', { soid: id, properties: newProps });
  }

  if (updatedInputs.length > 0) {
    let ids = [],
      values = [];

    for (let [id, value] of updatedInputs) {
      ids.push(id);
      values.push(value);
    }

    await requester.mutate('updateObjectProperties', { ids, values });
  }
}

/**
 * Updates a graph node with new details and parent relationships
 * @param id ID of the node to update
 * @param input New system object details
 */
export async function updateGraphNode(id: number, input: Partial<SystemObjectDetails>) {
  const { parents = [], path, ...rest } = input;
  const current = findGraphNode(id);

  if (!!current?.details?.name !== !!input?.name) {
    await requester.mutate('co4UpdateSystemObject', { id, hidden: !input?.name });
  }

  const currentPIds = current?.details?.parents?.map(({ id }) => id) || [];
  const pIds = parents.map((p) => p?.id);
  const parentsChanged = !deepCompare(currentPIds, pIds);

  if (parentsChanged) {
    await requester
      .mutate('co4MoveSystemObject', { id, parents: parents.map((p) => p?.id) })
      .then(() => moveNode(id, parents));
  }

  const res = await updateProperties(id, rest);

  updateNode(id, (state) => ({
    details: {
      ...state.details,
      ...rest,
      labels: rest?.labels ?? [],
      parents,
    },
  }));

  return res;
}

/**
 * Extracts system object references from a URL segment
 * @param segment URL segment containing hexadecimal IDs
 */
export function getSystemObjectRefs(segment: string): Array<number> {
  return segment
    .split('-')
    .map((x) => parseInt(x, 16))
    .filter((x) => !isNaN(x));
}

/**
 * Creates a URL segment from system object references
 * @param objects Array of system object references
 */
export function getSystemObjectLink(objects: Array<SystemObjectRef>) {
  return objects.map((obj) => obj.id.toString(16)).join('-');
}

/**
 * Gets a URL segment for the root node trail.
 * Returns an empty string when the user has no root objects so callers can branch
 * on it instead of crashing (see CO4-3489).
 */
export function getRootTrail() {
  const [root] = roots;
  return root === undefined ? '' : root.toString(16);
}

/**
 * Gets the path for an object by ID or nature instance ID
 * @param id System object ID
 * @param niid Nature instance ID
 */
export async function getObjectPath(id?: number, niid?: number) {
  if (typeof id !== 'number' && typeof niid !== 'number') {
    return '';
  }

  const object = findGraphNode(id);
  const path = object?.details?.path;

  if (path?.length) {
    return formatObjectPath(path);
  } else {
    const { path } =
      (await requester.query<{ path: number[] }>('co4GetObjectPath', { id: id ?? null, niid: niid ?? null })) ?? {};
    return formatObjectPath(path);
  }
}

/**
 * Formats an object path array into a URL-friendly string
 * @param path Array of system object IDs representing a path
 */
export function formatObjectPath(path: Array<number>) {
  return path.map((p) => p.toString(16)).join('-');
}

/**
 * Fetches references (back-links) for the given NIIDs and/or SOIDs.
 * Returns a flat, topologically-sorted list of ReferenceNodes.
 */
export async function getReferences(niids?: Array<number>, soids?: Array<number>): Promise<ReferenceResult> {
  const res = await requester.query<{ result: ReferenceResult }>('co4GetReferences', {
    niids: niids ?? null,
    soids: soids ?? null,
  });
  return res.result;
}

/**
 * Compares two reference nodes by name (same rules as sidebar navigation sortNodes).
 */
function sortReferenceNodes(a: ReferenceNode, b: ReferenceNode) {
  const nameA = (a.name ?? '').toLocaleLowerCase();
  const nameB = (b.name ?? '').toLocaleLowerCase();
  return nameA.localeCompare(nameB);
}

function sortReferenceTreeInPlace(nodes: Array<ReferenceTreeNode>) {
  nodes.sort(sortReferenceNodes);
  for (const node of nodes) {
    sortReferenceTreeInPlace(node.children);
  }
}

/** Sorts a flat reference list alphabetically by name (sidebar navigation order). */
export function sortReferenceNodesFlat(nodes: Array<ReferenceNode>): Array<ReferenceNode> {
  return [...nodes].sort(sortReferenceNodes);
}

/**
 * Assembles a flat topologically-sorted ReferenceNode list into a tree.
 * Because the list is sorted parents-first, a single pass suffices.
 * Sibling nodes are ordered alphabetically by name, matching the sidebar object tree.
 */
export function buildReferenceTree(flatNodes: Array<ReferenceNode>): Array<ReferenceTreeNode> {
  const map = new Map<number, ReferenceTreeNode>();
  const roots: Array<ReferenceTreeNode> = [];

  for (const node of flatNodes) {
    const treeNode: ReferenceTreeNode = { ...node, children: [] };
    map.set(node.soid, treeNode);

    if (node.parentSoid != null && map.has(node.parentSoid)) {
      map.get(node.parentSoid)!.children.push(treeNode);
    } else {
      roots.push(treeNode);
    }
  }

  sortReferenceTreeInPlace(roots);
  return roots;
}

// --- Home dashboard -----------------------------------------------------------
// The widgets on the user's home dashboard, held in one user setting.
//
// Core keeps a setting's value opaque, so there is nothing server-side to address
// a single widget by: the whole layout is read and written as one value. A future
// dashboard product will own the widgets properly and this setting will shrink to
// a reference to it.
//
// Kept here, not in Piral state, because the cards offering "add to dashboard"
// live in lib-utils and cannot reach Piral.

/** The setting the layout lives under. A frontend convention; core does not know it. */
const HOME_DASHBOARD_KEY = 'home_dashboard';

export const landingPageDashboard = createStore<LandingPageDashboardState>(() => ({
  status: 'idle',
  dashboard: null,
  pending: [],
}));

/**
 * Identifies a widget. There are no server-side ids, and type alone is not
 * enough: the same widget can be on the dashboard once per object.
 */
export function dashboardWidgetKey(widgetType: string, objectId?: number | null): string {
  return `${widgetType}:${objectId ?? ''}`;
}

const isSameWidget = (widget: DashboardWidget, widgetType: string, objectId?: number | null) =>
  widget.widgetType === widgetType && (widget.objectId ?? null) === (objectId ?? null);

/** The home dashboard widget for this type and object, if it is there. */
export function findLandingPageWidget(widgetType: string, objectId?: number | null): DashboardWidget | undefined {
  const { dashboard } = landingPageDashboard.getState();
  return dashboard?.widgets.find((widget) => isSameWidget(widget, widgetType, objectId));
}

/**
 * The stored value as a layout we can rely on.
 *
 * Core does not validate what goes in a setting, so anything could come back -
 * including a shape written by an older version of this code. Whatever does not
 * look like a widget list is treated as an empty dashboard rather than throwing,
 * so a bad value costs the user their layout and not the page.
 */
function toDashboard(value: unknown): LandingPageDashboard {
  const widgets = (value as LandingPageDashboard | null)?.widgets;
  return { widgets: Array.isArray(widgets) ? widgets : [] };
}

function setPending(key: string, pending: boolean) {
  landingPageDashboard.setState((state) => ({
    pending: pending
      ? state.pending.includes(key)
        ? state.pending
        : [...state.pending, key]
      : state.pending.filter((entry) => entry !== key),
  }));
}

// Shared so the many cards mounting at once make one request, not one each.
let landingPageDashboardRequest: Promise<LandingPageDashboard | null> | null = null;

/**
 * Loads the user's home dashboard. A user who has never arranged one has no
 * setting, which reads as an empty dashboard rather than an error.
 * Concurrent callers share the in-flight request.
 */
export function loadLandingPageDashboard(): Promise<LandingPageDashboard | null> {
  if (landingPageDashboardRequest) {
    return landingPageDashboardRequest;
  }

  landingPageDashboard.setState({ status: 'loading', error: undefined });

  landingPageDashboardRequest = requester
    .query<{ setting: { value: unknown } | null }>('coreGetUserSetting', { key: HOME_DASHBOARD_KEY })
    .then(({ setting }) => {
      const dashboard = toDashboard(setting?.value);
      landingPageDashboard.setState({ status: 'ready', dashboard, error: undefined });
      return dashboard;
    })
    .catch((err) => {
      // Kept in state, not thrown: this runs at startup and must not block it.
      landingPageDashboard.setState({
        status: 'error',
        dashboard: null,
        error: err instanceof Error ? err.message : String(err),
      });
      return null;
    })
    .finally(() => {
      // Cleared so a later call can retry after a failure.
      landingPageDashboardRequest = null;
    });

  return landingPageDashboardRequest;
}

/** The layout to change, loading it first if startup has not got there yet. */
async function requireLandingPageDashboard(): Promise<LandingPageDashboard> {
  const loaded = landingPageDashboard.getState().dashboard ?? (await loadLandingPageDashboard());

  if (!loaded) {
    throw new Error(landingPageDashboard.getState().error ?? 'Your dashboard could not be loaded.');
  }

  return loaded;
}

/**
 * Applies a change to the layout and writes the whole thing back.
 *
 * `key` is marked busy while the write is in flight. The store is only updated
 * once the write has been accepted, so a failure leaves what is on screen as
 * what is actually stored.
 */
async function writeLandingPageDashboard(
  key: string,
  change: (widgets: ReadonlyArray<DashboardWidget>) => Array<DashboardWidget>,
): Promise<LandingPageDashboard> {
  const current = await requireLandingPageDashboard();
  const next: LandingPageDashboard = { widgets: change(current.widgets) };

  setPending(key, true);

  try {
    await requester.mutate<{ setting: { value: unknown } }>('coreSetUserSetting', {
      key: HOME_DASHBOARD_KEY,
      value: next,
    });
    landingPageDashboard.setState({ status: 'ready', dashboard: next, error: undefined });
    return next;
  } finally {
    setPending(key, false);
  }
}

/**
 * Adds a widget for the given object. One that is already there is left alone, so
 * a repeated click cannot duplicate it or reset the user's layout.
 */
export async function addWidgetToLandingPage(
  widgetType: string,
  objectId?: number | null,
  options: {
    /** The card's name, so copies of one widget type can be told apart. */
    title?: string;
    alignment?: WidgetAlignment;
    size?: WidgetSize;
    config?: Record<string, unknown>;
  } = {},
): Promise<LandingPageDashboard> {
  return writeLandingPageDashboard(dashboardWidgetKey(widgetType, objectId), (widgets) => {
    if (widgets.some((widget) => isSameWidget(widget, widgetType, objectId))) {
      return [...widgets];
    }

    const added: DashboardWidget = {
      widgetType,
      objectId: objectId ?? null,
      title: options.title ?? null,
      config: options.config ?? null,
      alignment: options.alignment ?? 'LEFT',
      size: options.size ?? 'MEDIUM',
      // Onto the end, past whatever is there now.
      sortOrder: widgets.reduce((highest, widget) => Math.max(highest, widget.sortOrder), -1) + 1,
    };

    return [...widgets, added];
  });
}

/** Changes a widget's placement or name. Only the given fields move. */
export async function updateLandingPageWidget(
  widget: Pick<DashboardWidget, 'widgetType'> & { objectId?: number | null },
  changes: {
    alignment?: WidgetAlignment;
    size?: WidgetSize;
    sortOrder?: number;
    title?: string;
    config?: Record<string, unknown>;
  },
): Promise<LandingPageDashboard> {
  return writeLandingPageDashboard(dashboardWidgetKey(widget.widgetType, widget.objectId), (widgets) =>
    widgets.map((entry) =>
      isSameWidget(entry, widget.widgetType, widget.objectId) ? { ...entry, ...changes } : entry,
    ),
  );
}

/** Removes a widget. One that is already gone leaves the layout as it is. */
export async function removeLandingPageWidget(
  widget: Pick<DashboardWidget, 'widgetType'> & { objectId?: number | null },
): Promise<LandingPageDashboard> {
  return writeLandingPageDashboard(dashboardWidgetKey(widget.widgetType, widget.objectId), (widgets) =>
    widgets.filter((entry) => !isSameWidget(entry, widget.widgetType, widget.objectId)),
  );
}
